import { useFormik } from "formik";
import { CInput } from "../components";
import {
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { createUserSchema } from "../utils/schemas";
import { AuthModel } from "../services";
import { FaCopy } from "react-icons/fa6";

export default function CreateUser() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const onCreateUser = async (values) => {
    try {
      await AuthModel.signup(values);
      toast({
        description: "Account created successfully!",
        status: "success",
        isClosable: true,
      });
      onOpen();
    } catch (error) {
      if (error.response.status === 409) {
        toast({
          description: error.response.data.message,
          status: "error",
          isClosable: true,
        });
      } else {
        toast({
          description: "Something went wrong!",
          status: "error",
          isClosable: true,
        });
      }
    }
  };

  const generatePassword = () => {
    let allowedChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"; // capital characters
    allowedChars += "abcdefghijklmnopqrstuvwxyz"; // small characters
    allowedChars += "0123456789"; // numbers
    allowedChars += "!@#$%^&*()_+-=[]{}|;:,.<>?"; // special characters
    let passwordLength = 12;

    let generatedPass = "";
    for (let i = 0; i < passwordLength; i++) {
      const randomIndex = Math.floor(Math.random() * allowedChars.length);
      generatedPass += allowedChars[randomIndex];
    }
    return generatedPass;
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: generatePassword(),
    },
    validationSchema: createUserSchema,
    onSubmit: onCreateUser,
  });

  return (
    <>
      <Heading mb={2} fontSize={{ base: "xl", md: "3xl" }}>
        Create User
      </Heading>
      <SimpleGrid columns={{ sm: 1, md: 2 }} gap="3">
        <CInput
          label="First Name"
          name="firstName"
          placeholder="Enter First Name"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          errors={formik.errors.firstName}
          touched={formik.touched.firstName}
        />
        <CInput
          label="Last Name"
          name="lastName"
          placeholder="Enter Last Name"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          errors={formik.errors.lastName}
          touched={formik.touched.lastName}
        />
        <CInput
          label="Email"
          name="email"
          placeholder="Enter Email"
          value={formik.values.email}
          onChange={formik.handleChange}
          errors={formik.errors.email}
          touched={formik.touched.email}
        />
        <CInput
          type="password"
          label="Password"
          name="password"
          placeholder="Enter Password"
          value={formik.values.password}
          onChange={formik.handleChange}
          errors={formik.errors.password}
          touched={formik.touched.password}
        />
      </SimpleGrid>
      <Button
        type="submit"
        onClick={formik.handleSubmit}
        isLoading={formik.isSubmitting}
        loadingText="Loading..."
        bgColor="black"
        borderRadius="lg"
        fontWeight="medium"
        py="6"
        px="7"
        mt="4"
      >
        Create
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          formik.resetForm();
          onClose();
          formik.setFieldValue("password", generatePassword());
        }}
        size="xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy Credentials</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              mb={3}
            >
              <Text fontSize="sm">
                This popup is visible only once so{" "}
                <strong>DO COPY AND SAVE</strong> the credentials.
              </Text>
              <Button
                type="button"
                bgColor="black"
                borderRadius="lg"
                fontWeight="medium"
                size="sm"
                p="4"
                ms="auto"
                rightIcon={<FaCopy />}
                onClick={async () => {
                  let textToCopy = `Email: ${formik.values.email}\nPassword: ${formik.values.password}`;
                  await navigator.clipboard.writeText(textToCopy);
                  toast({
                    description: "Text Copied to Clipboard!",
                    status: "success",
                    isClosable: true,
                  });
                }}
              >
                Copy
              </Button>
            </Flex>
            <SimpleGrid columns={{ sm: 1, md: 2 }} gap="3" mb={4}>
              <CInput
                label="Email"
                name="email"
                placeholder="Enter Email"
                value={formik.values.email}
                readOnly={true}
              />
              <CInput
                type="password"
                label="Password"
                name="password"
                placeholder="Enter Password"
                value={formik.values.password}
                readOnly={true}
              />
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
